import React from "react";
import {
  Stack,
  Box,
  Group,
  Switch,
  Collapse,
  Text,
  Select,
  NumberInput,
  Checkbox,
} from "@mantine/core";
// @ts-ignore
import chevronDown from "../../../icons/chevron-down.png";
// @ts-ignore
import chevronUp from "../../../icons/chevron-up.png";
import WeekdayToggle from "./WeekdayToggle";
import { DayAllowedFlags, extractDayFlags } from "./dayAvailability";
// @ts-ignore — staffUtils.js predates the TS migration
import { isBookableActiveEmployee } from "../../../utils/staffUtils";

interface StaffServiceSettingsData {
  id: string | null;
  enabled: boolean;
  price: number | null;
  duration: string;
  processingTime: string;
  finishingTime: string;
  bufferTime: string;
  startsAt: boolean;
  isOpen: boolean;
  is_self_servicable: boolean;
}

function generateDurationOptions() {
  const options = [];
  for (let minutes = 5; minutes <= 240; minutes += 5) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label =
      hours > 0
        ? `${hours} hr${hours > 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
        : `${mins} min`;
    options.push({ value: `${minutes}`, label });
  }
  return options;
}

interface StaffSelectionStepProps {
  formData: any;
  setFormData: (data: any) => void;
  employees: any[];
  isMobile: boolean;
}

export default function StaffSelectionStep({
  formData,
  setFormData,
  employees,
  isMobile,
}: StaffSelectionStepProps) {
  // Pricing basis is a salon-service-level setting (read-only inheritance
  // here) — surface it as a hint on the per-staff price inputs.
  const priceBasisHint =
    formData.price_basis === "per_syringe"
      ? "per syringe"
      : formData.price_basis === "per_unit"
        ? "per unit"
        : undefined;

  const getEmployeeSettings = (
    employeeId: string,
  ): StaffServiceSettingsData => {
    return (
      formData.employeeSettings[employeeId] || {
        id: null,
        enabled: true,
        price: formData.price,
        duration: formData.duration,
        processingTime: formData.processingTime ?? "0",
        finishingTime: formData.finishingTime ?? "0",
        bufferTime: formData.bufferTime ?? "0",
        startsAt: formData.startsAt,
        isOpen: false,
        is_self_servicable: formData.is_self_servicable,
        // Inherit the salon-level available days by default
        ...extractDayFlags(formData),
      }
    );
  };

  const handleEmployeeDaysChange = (
    employeeId: string,
    flags: DayAllowedFlags,
  ) => {
    setFormData({
      ...formData,
      employeeSettings: {
        ...formData.employeeSettings,
        [employeeId]: {
          ...getEmployeeSettings(employeeId),
          ...flags,
        },
      },
    });
  };

  const toggleEmployeeDropdown = (employeeId: string) => {
    const employeeSettings = getEmployeeSettings(employeeId);
    setFormData({
      ...formData,
      employeeSettings: {
        ...formData.employeeSettings,
        [employeeId]: {
          ...employeeSettings,
          isOpen: !employeeSettings.isOpen,
        },
      },
    });
  };

  return (
    <Stack gap={isMobile ? "sm" : "md"} mt="md">
      <Text size="sm" c="dimmed">
        Select which employees can perform this service. Each employee can have
        their own pricing and duration options.
      </Text>

      {/* Display-only filter: a hidden-calendar staff has no business being
          assignable to a service (they can't be booked). No includeId
          escape hatch — the wizard saves into a parent settings map keyed
          by employee id, so a currently-assigned-but-now-hidden staff's
          stored settings are NOT silently dropped when we stop rendering
          their row. The toggle is reversible (the backend preserves
          staff_services across the flip), so re-enabling immediately
          restores the row + their existing settings. */}
      {employees.filter(isBookableActiveEmployee).map((employee) => (
        <Box key={employee.id} mt={isMobile ? "xs" : "sm"}>
          <Group gap={isMobile ? "sm" : "md"}>
            <Group>
              <Switch
                size={isMobile ? "sm" : "md"}
                label={`${employee.first_name} ${employee.last_name} - ${employee.title}`}
                checked={getEmployeeSettings(employee.id).enabled}
                onChange={(event) => {
                  event.stopPropagation();
                  const isChecked = event.currentTarget.checked;
                  const currentSettings = getEmployeeSettings(employee.id);

                  // If freshly enabling (was disabled, now enabled), populate with service defaults
                  if (isChecked && !currentSettings.enabled) {
                    setFormData({
                      ...formData,
                      employeeSettings: {
                        ...formData.employeeSettings,
                        [employee.id]: {
                          id: currentSettings.id,
                          enabled: true,
                          isOpen: true,
                          price: formData.price,
                          duration: formData.duration,
                          processingTime: formData.processingTime ?? "0",
                          finishingTime: formData.finishingTime ?? "0",
                          bufferTime: formData.bufferTime ?? "0",
                          startsAt: formData.startsAt,
                          is_self_servicable: formData.is_self_servicable,
                          // Inherit the salon-level available days on enable
                          ...extractDayFlags(formData),
                        },
                      },
                    });
                  } else {
                    // Otherwise just toggle enabled state
                    setFormData({
                      ...formData,
                      employeeSettings: {
                        ...formData.employeeSettings,
                        [employee.id]: {
                          ...currentSettings,
                          enabled: isChecked,
                          isOpen: isChecked,
                        },
                      },
                    });
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Group>
            <img
              src={
                getEmployeeSettings(employee.id).isOpen
                  ? chevronUp
                  : chevronDown
              }
              alt="toggle"
              width="20"
              height="20"
              style={{ cursor: "pointer", alignSelf: "flex-start" }}
              onClick={() => toggleEmployeeDropdown(employee.id)}
            />
          </Group>

          <Collapse in={getEmployeeSettings(employee.id).isOpen}>
            <Box pl={isMobile ? 30 : 50}>
              <Stack gap="sm">
                <Group grow align="flex-end" gap={isMobile ? "sm" : "md"}>
                  <Select
                    label="Base Duration"
                    value={getEmployeeSettings(employee.id).duration}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            duration: value,
                          },
                        },
                      });
                    }}
                    data={generateDurationOptions()}
                    style={{ maxWidth: 160 }}
                  />
                  <Select
                    label="Processing Time"
                    value={
                      getEmployeeSettings(employee.id).processingTime ?? "0"
                    }
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            processingTime: value,
                          },
                        },
                      });
                    }}
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    style={{ maxWidth: 160 }}
                  />
                </Group>
                <Group grow align="flex-end" gap={isMobile ? "sm" : "md"}>
                  <Select
                    label="Finishing Time"
                    value={
                      getEmployeeSettings(employee.id).finishingTime ?? "0"
                    }
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            finishingTime: value,
                          },
                        },
                      });
                    }}
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    style={{ maxWidth: 160 }}
                  />
                  <Select
                    label="Buffer Time"
                    value={getEmployeeSettings(employee.id).bufferTime ?? "0"}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            bufferTime: value,
                          },
                        },
                      });
                    }}
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    style={{ maxWidth: 160 }}
                  />
                </Group>
                <Group grow align="flex-end" gap={isMobile ? "sm" : "md"}>
                  <NumberInput
                    label="Price"
                    description={priceBasisHint}
                    value={getEmployeeSettings(employee.id).price ?? undefined}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            price: value ?? null,
                          },
                        },
                      });
                    }}
                    min={0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ maxWidth: 160 }}
                  />
                  <Checkbox
                    label="Starts At"
                    checked={getEmployeeSettings(employee.id).startsAt}
                    onChange={(event) => {
                      const isChecked = event.currentTarget.checked;
                      setFormData({
                        ...formData,
                        employeeSettings: {
                          ...formData.employeeSettings,
                          [employee.id]: {
                            ...getEmployeeSettings(employee.id),
                            startsAt: isChecked,
                          },
                        },
                      });
                    }}
                    mt={25}
                  />
                </Group>
                <Switch
                  label="Show in online booking"
                  checked={getEmployeeSettings(employee.id).is_self_servicable}
                  onChange={(event) => {
                    const isChecked = event.currentTarget.checked;
                    setFormData({
                      ...formData,
                      employeeSettings: {
                        ...formData.employeeSettings,
                        [employee.id]: {
                          ...getEmployeeSettings(employee.id),
                          is_self_servicable: isChecked,
                        },
                      },
                    });
                  }}
                />
                <WeekdayToggle
                  label="Available days"
                  tooltip="Clear a day to stop this staff member from being booked for this service that day."
                  value={extractDayFlags(getEmployeeSettings(employee.id))}
                  onChange={(flags) =>
                    handleEmployeeDaysChange(employee.id, flags)
                  }
                />
              </Stack>
            </Box>
          </Collapse>
        </Box>
      ))}
    </Stack>
  );
}
